/**
 * THE one rule for reading a booking's journey ends out of `booking_stops`.
 *
 * `seq` is assigned by POSITION (bookings-repo's `insertStops`), so a booking
 * with a SINGLE stop and no recorded role is genuinely ambiguous on the row: a
 * pickup-only capture and a dropoff-only capture both
 * persist one row at seq 0. Until T549 M4 each producer guessed for itself and
 * they guessed in OPPOSITE directions — the quote SMS said "from 12 Main St"
 * and the deposit-received SMS said "to 12 Main St" for the same booking, to
 * the same customer. One reader, one rule, so they cannot disagree again.
 *
 * THE DISCRIMINATOR IS WHICH END THE STOPS WRITER POPULATED — not "wedding vs
 * everything else". The first cut of this rule asked "is it a wedding?" and
 * sent every other type to the destination arm, which produced a second defect
 * of the same class immediately: a GOLF lead's lone stop is the customer's own
 * front door, so the quote text read "your quote for Golf Transfer (to 12 Main
 * St, Bray)" — the bus taking them to their own door. The question that has an
 * answer is: for THIS service, which journey end does the capture actually
 * collect? Both writers push the PICKUP first (`buildStops` in
 * core/normalise.ts: pickup → middles → dropoff; `journeyStops` in
 * client/lib/actions.ts: pickupEndPlace → middles → dropoffEndPlace), so seq 0
 * is the pickup SLOT, and a lone row is a pickup unless the service captures no
 * pickup at all or the writer put a destination there instead.
 *
 * Every entry in LONE_STOP_ROLE below is derived from three ground truths, not
 * from assumption: the form's required fields (REQUIRED_BY_SVC in
 * site/src/generated/inquiry-schema.ts), the fields the form actually SHOWS
 * (`data-svc` in site/src/components/InquiryForm.astro — `applyService`
 * DISABLES a hidden input, so a field a service's branch omits cannot even be
 * posted), and what `buildStops` / `journeyStops` write from them.
 *
 * The rule, in the order it decides:
 *
 *   1. Two or more stops — seq 0 is the pickup, the last is the destination.
 *      Unchanged, and the case the record can actually answer.
 *   1b. A LONE stop whose `role` (v20) NAMES an end — that end, full stop. This
 *      is the one arm that is a FACT rather than an inference, so it beats every
 *      rule below it including the venue and airport-town heuristics, which
 *      exist only to reconstruct what a role would have told them. It is safe
 *      against rule 2 by construction: `venue` only ever reaches a stop through
 *      `dropoffEndPlace` (client/store/selectors.ts ranks it first for every
 *      manual service) and `pickupEndPlace` never reads it, while `buildStops`
 *      does not read `venue` at all — so a post-v20 venue row always carries
 *      the destination role and can never contradict rule 2.
 *   2. A LONE stop that IS the booking's venue — the DESTINATION, whatever the
 *      booking type. `venue` is only ever WRITTEN as a drop-off end: the
 *      kiosk's `dropoffEndPlace` ranks it first for every manual service, and
 *      the column is defined as "structured venue (wedding) / destination
 *      (private-tour)" (core/migrations.ts). This is the ordinary wedding — the
 *      public form has no pickup field for a wedding (it requires `venue` and
 *      offers no pickup), so the lead persists exactly one stop and it is the
 *      venue. Reading that as the pickup told the customer the bus was
 *      collecting them at the church.
 *   3. Otherwise — LONE_STOP_ROLE[booking_type], stated per type below, with
 *      an unenumerated token taking UNKNOWN_LONE_STOP_ROLE.
 *
 * Note that 2 keys on the venue MATCHING the stop, not merely on a venue
 * existing: a legacy wedding row written before T540 can carry a venue on the
 * booking and a typed pickup street in its one stop, and that street is a
 * pickup (it is what reopenBooking's street-labelled-pickup pin is built on).
 *
 * Rules 2 and 3 do NOT make a lone stop unambiguous — nothing about a pre-v20
 * row can. They make every producer wrong or right IDENTICALLY, which is what a
 * customer can actually check. Rule 1b IS the durable fix (v20's
 * `booking_stops.role`, T608 M6c) and it applies from that migration onward;
 * rules 2 and 3 stay because every row written before it has `role IS NULL` and
 * no backfill can invent one, so deleting them would silently re-answer every
 * booking already in the database.
 *
 * Lives in `shared/` rather than `core/` because the CLIENT is the fourth
 * reader: `reopenBooking` re-hydrates Helen's screen from the same rows, and
 * `src/client` may import only `src/client` and `src/shared` (core reaches for
 * `pg`, which cannot enter the browser bundle). A rule with a reader on the
 * other side of that boundary is a shared rule.
 */
import type { BookingType } from './passengers.js';

type LoneStopRole = 'pickup' | 'destination';

/**
 * THE closed vocabulary of `booking_stops.role` (v20).
 *
 * The two ENDS reuse the `LoneStopRole` tokens deliberately, so the reader below
 * is a LOOKUP rather than a translation table that could drift out of step with
 * the convention it replaces. A MIDDLE needs its own token because it is neither
 * end — and it is not decorative: a lone middle is reachable (a POST to
 * /api/bookings carrying `stops` but neither `pickup` nor `dropoff` persists one
 * row that is a middle), which is why the reader's role branch must be TOTAL and
 * treat 'middle' exactly like an unrecorded role.
 *
 * Enforced at the WIRE boundary (`parseStops`, server/app.ts) rather than by a
 * DB CHECK — see v20's own comment in core/migrations.ts for why.
 */
export const STOP_ROLES = ['pickup', 'middle', 'destination'] as const;
export type StopRole = (typeof STOP_ROLES)[number];

/** Is this an in-vocabulary role token? Used by the wire sanitizer, which turns
 *  anything else into NULL (a stray token must never cost the operator her
 *  quote — the reader degrades to the legacy inference for it). */
export function isStopRole(v: unknown): v is StopRole {
  return typeof v === 'string' && (STOP_ROLES as readonly string[]).includes(v);
}

/**
 * Where a booking's LONE stop sits, per `booking_type`.
 *
 * Typed as a TOTAL Record over BookingType (the six members of the
 * `booking_type` DB enum, migrations.ts), so adding a service to the registry
 * fails the build here until someone states its answer — rather than letting it
 * inherit a default that was never considered for it. That build failure is the
 * whole point: the golf defect existed because a default answered for a service
 * nobody had checked.
 *
 * The KEY vocabulary is `booking_type`, and every token below except
 * 'recurring_contract' is spelled identically as a serviceId — which is what
 * lets reopenBooking pass the UNFOLDED service ('nursing-home') through the
 * same table and land on the same answer as the folded row ('local-other').
 */
const LONE_STOP_ROLE: Readonly<Record<BookingType, LoneStopRole>> = {
  // BOTH ends always exist on a real airport capture: the form requires
  // direction + terminal and the submit handler composes the terminal into the
  // end the address block does not occupy (InquiryForm.astro, "(ii) airport
  // only: the terminal is one END of the journey"), while the kiosk appends
  // DUBLIN_AIRPORT_LABEL whenever a pickup exists (journeyStops). So a lone
  // airport row is a capture whose PICKUP half is missing, and the end that
  // survived is the airport. Pinned since before this module existed:
  // test/core/deposit-received-alert.spec.ts, 'Dublin Airport T2'.
  airport: 'destination',
  // THE DEFECT this arm fixes. REQUIRED_BY_SVC.golf is
  // pickup/town/pickupEircode/course and `dropoff` carries data-svc="local-other"
  // — the golf branch has no drop-off field at all, so `dropoff` is disabled and
  // cannot be posted. `course` is its own inquiries column and buildStops never
  // reads it, so a golf lead persists exactly ONE stop: the customer's own
  // address. (A kiosk golf job also lands at position 0 — golf is grid-priced,
  // so pickupEndPlace supplies the route label and dropoffEndPlace is empty.)
  golf: 'pickup',
  // Rule 2 above already answers the ORDINARY wedding, whose lone stop is the
  // venue. What is left here is the walk-in street Helen types without engaging
  // VenueControl: that street is where she collects the party, and naming it as
  // their destination is the T540/516 residual this fixes.
  wedding: 'pickup',
  // RETIRED (service-schema RETIRED_SERVICES — off the public form and out of
  // KIOSK_SERVICE_IDS), so only legacy rows reach this arm. Same shape as a
  // wedding: requiredFields are pickup/town/pickupEircode/itinerary with no
  // drop-off field, and its DESTINATION rides `venue` (VenueControl renders a
  // free-text "Destination" for private-tour), which rule 2 already claims.
  'private-tour': 'pickup',
  // The form requires BOTH ends (REQUIRED_BY_SVC['local-other'] ends in
  // `dropoff`), so a public local-other lead is never lone. A lone row is
  // kiosk-origin: a p2p job with a pickup chosen and no drop-off end yet, or
  // the nursing-home fold (serviceToBookingType maps 'nursing-home' →
  // 'local-other'), whose lone stop is the home Helen typed. Both are position
  // 0, which is also what reopenBooking puts back in the pickup field.
  'local-other': 'pickup',
  // Phase-2 seed (Round 14): no form branch, no kiosk tile, no descriptor and
  // no writer today, so there is no capture to derive from. Takes the POSITION
  // answer both writers give — pickup first — rather than a guess about a flow
  // that does not exist yet. Whoever builds the recurring flow owns this line.
  recurring_contract: 'pickup',
};

/**
 * A `booking_type` no registry names. Reachable only through POST /api/bookings,
 * which takes the service as free text — `serviceToBookingType` folds an
 * unknown SERVICE to 'local-other' long before it reaches a row. Same answer as
 * local-other and for the same reason: seq 0 is the writer's pickup slot, so
 * position is the only evidence an unenumerated token leaves.
 */
const UNKNOWN_LONE_STOP_ROLE: LoneStopRole = 'pickup';

/** `Object.hasOwn`, not a bare index — LONE_STOP_ROLE is a plain object literal,
 *  so `['__proto__']` returns Object.prototype and `['constructor']` the Object
 *  function. Both are truthy, so a `??` tail could never fire and a lone stop
 *  would take neither arm knowingly. Same guard, same reason, as
 *  `bookingTypeLabel` in status-schema.ts. */
function loneStopRole(bookingType: string | null): LoneStopRole {
  if (bookingType === null || !Object.hasOwn(LONE_STOP_ROLE, bookingType)) return UNKNOWN_LONE_STOP_ROLE;
  return LONE_STOP_ROLE[bookingType as BookingType];
}

/** The minimum a stop row must carry to be placed. Callers pass their OWN row
 *  shape (the alert mappers' `{seq,address}`, the client's full capture row)
 *  and get that same shape back, so the reopen path keeps its eircode / address
 *  line / town without a second traversal. */
export type JourneyStopRow = {
  seq: number;
  address: string;
  town?: string | null;
  /** v20's recorded role. `string`, not `StopRole`: this is a value read back
   *  out of a TEXT column with no CHECK, so the type must admit anything the
   *  database can hold. NULL / absent = a pre-v20 row, or a caller whose row
   *  shape does not select the column. */
  role?: string | null;
};

/** The fields of the booking row the rule reads. Every caller holds a row that
 *  structurally satisfies this (BookingRow and the client's ApiBookingDetail). */
export type JourneyBooking = { booking_type: string | null; venue: string | null };

export type JourneyEnds<T> = {
  /** Where the party is collected, or null when the record cannot say. */
  pickup: T | null;
  /** Where the party is going, or null when the record cannot say. */
  dropoff: T | null;
};

/** Same place, allowing for the casing/padding drift between a typed venue and
 *  the stop label built from it. Deliberately not fuzzy: a near-miss must read
 *  as "not the venue" (a pickup), never as a silent venue match.
 *
 *  Both sides are capped at 200 by the SAME sanitizer at the write boundary
 *  (`sanitizeVenue` and `parseStops` in server/app.ts), so a long venue cannot
 *  drift out of this comparison — the risk that made a 205-character venue fall
 *  to the pickup arm, which is pinned in app.spec.ts. */
function samePlace(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/**
 * Is this stop row the booking's VENUE (rather than a journey end Helen typed)?
 * Rule 2 above: `venue` is only ever written as a drop-off end, so a stop that
 * equals it IS that end, whatever the booking type says.
 */
/**
 * The end this row's WRITER recorded (v20), or null when the record does not say.
 *
 * TOTAL by construction, which is the whole reason this is a function and not an
 * index: a pre-v20 row (null), a row from a caller that does not select the
 * column (undefined), a lone MIDDLE ('middle' — reachable, see STOP_ROLES) and
 * any off-vocabulary token all read as "no recorded end", so every one of them
 * falls through to the legacy inference instead of resolving to neither end.
 */
function recordedEndRole(stop: JourneyStopRow): LoneStopRole | null {
  return stop.role === 'pickup' || stop.role === 'destination' ? stop.role : null;
}

function isVenueStop(booking: JourneyBooking, stop: JourneyStopRow): boolean {
  return booking.venue != null && booking.venue.trim() !== '' && samePlace(booking.venue, stop.address);
}

/**
 * Is this lone AIRPORT stop the customer's own address rather than the terminal?
 *
 * The `airport: 'destination'` row assumes the surviving end is the terminal,
 * which holds for a lead composed by InquiryForm.astro. It does NOT hold for the
 * endpoint: `REQUIRED_BY_SVC` is client-side only, and `server/inquiry.ts` types
 * `dropoff` as a plain optional string with no per-service check — so a POST
 * carrying `direction`/`terminal`/`pickup` and no `dropoff` persists ONE row
 * that is the customer's pickup.
 *
 * The record already discriminates it: `buildStops` (core/normalise.ts) and
 * `journeyStops` (client/lib/actions.ts) write `town` on the ADDRESS end and
 * never on the terminal, which is the same invariant reopenBooking's T515 guard
 * is built on ("a terminal row's town is always null").
 *
 * CORRECTION (T608 M6c security review). This note used to frame the downstream
 * guard's vacuity as a consequence of MIS-reading the row, and that was only half
 * of it. The guard read `pickupStop?.town == null`, which is vacuously true
 * whenever the rule hands back no pickup at all — and after rule 1b the CORRECT
 * reading of this same shape (a lone row recorded `role: 'destination'`) also
 * returns no pickup. So the truthful answer walked into the vacuous clause too,
 * which is a regression v20 introduced rather than one it inherited. The guard is
 * now hardened against it (`pickupStop !== null && pickupStop.town == null`), so
 * "the record cannot say where the pickup is" lands the operator on the Location
 * screen instead of promoting the customer's town to a rate key. Either way — a
 * mis-read row pre-v20, or a correctly-read one after it — the failure was the
 * same: reopen pointed both ends at the same address and dropped the airport from
 * the journey entirely.
 *
 * A stop with no `town` key at all (a caller whose row shape omits it) stays on
 * the terminal reading, so the ordinary two-end capture is untouched.
 */
function isLoneAirportAddressEnd(booking: JourneyBooking, stop: JourneyStopRow): boolean {
  return booking.booking_type === 'airport' && stop.town != null && stop.town.trim() !== '';
}

/**
 * Resolve the pickup/destination ends of a persisted journey.
 *
 * Sorts by `seq` rather than trusting arrival order: `getBookingDetail` selects
 * `ORDER BY seq`, but this is the one place the ordering is load-bearing and a
 * caller holding stops from anywhere else must get the same answer.
 */
export function resolveJourneyEnds<T extends JourneyStopRow>(
  booking: JourneyBooking,
  stops: readonly T[],
): JourneyEnds<T> {
  const ordered = [...stops].sort((a, b) => a.seq - b.seq);
  const first = ordered[0];
  if (!first) return { pickup: null, dropoff: null };

  const last = ordered.length > 1 ? ordered[ordered.length - 1]! : null;
  if (last) return { pickup: first, dropoff: last };

  // Lone stop — the ambiguous case the whole module exists for. Order matters:
  // the RECORDED role (v20) is a fact and beats every inference below it; then a
  // venue is only ever a drop-off end, and an airport row carrying a town is
  // only ever an address end, so both beat the per-type default.
  const role =
    recordedEndRole(first) ??
    (isVenueStop(booking, first)
      ? 'destination'
      : isLoneAirportAddressEnd(booking, first)
        ? 'pickup'
        : loneStopRole(booking.booking_type));
  return role === 'pickup' ? { pickup: first, dropoff: null } : { pickup: null, dropoff: first };
}

/**
 * The stops BETWEEN the two ends, in journey order (T1679, board #1679).
 *
 * ⛔ A SIBLING OF `resolveJourneyEnds`, NOT A CHANGE TO IT. Adding a `middles`
 * key to `JourneyEnds` would have been tidier and is the wrong trade: that type
 * has four readers across two packages (the quote message, the deposit-received
 * message, `reopenBooking` on the client, and the evidence arm), and none of
 * them wants the middles. An additive export changes nobody.
 *
 * WHY IT EXISTS: `resolveJourneyEnds` returns `first` and `last` and silently
 * drops everything between, so a journey with extra stops - which the public
 * form captures as `stop1`..`stop4` and `normalise` persists with
 * `role: 'middle'` - reached the customer as a bare two-end route. A customer
 * who asked for three pickups had no way to tell the quote covered them.
 *
 * ⚠️ THE SAME `seq` SORT, and it is load-bearing for the same reason it is in
 * the function above: `getBookingDetail` selects `ORDER BY seq`, but a caller
 * holding stops from anywhere else must get the same answer, and here a wrong
 * order is a wrong journey rather than a wrong end.
 *
 * Correct by construction at the boundaries: 0, 1 and 2 stops all yield `[]`,
 * because `slice(1, -1)` of a shorter-than-3 array is empty. The lone-stop
 * ambiguity the module exists for therefore never reaches this function.
 */
export function journeyMiddles<T extends JourneyStopRow>(stops: readonly T[]): T[] {
  return [...stops].sort((a, b) => a.seq - b.seq).slice(1, -1);
}
